import React, { useMemo } from 'react';
import { Select, ConfigProvider, theme } from 'antd';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Isolated Ant Design Dropdown Component
 * Does not affect existing code - use only where needed
 */
const AntDropdownComponent = ({ 
  value, 
  onChange, 
  options = [],
  placeholder = 'Select option',
  disabled = false,
  showSearch = true,
  allowClear = true,
  className = '',
  style = {},
  loading = false,
  ...props 
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const themeConfig = useMemo(() => ({
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: '#3b82f6',
      borderRadius: 12,
      colorBgContainer: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
      colorBgElevated: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
      colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.15)',
      colorText: isDark ? '#ffffff' : '#0f172a',
      colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(71, 85, 105, 0.55)',
      colorTextQuaternary: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(148, 163, 184, 0.6)',
      colorTextTertiary: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(71, 85, 105, 0.8)',
      colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.8)' : '#1f2937',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: 14,
      controlHeight: 40,
      controlOutline: 'none',
    },
    components: {
      Select: {
        optionSelectedBg: isDark ? 'rgba(59, 130, 246, 0.3)' : '#eef2ff',
        optionActiveBg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.08)',
        colorBgElevated: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#0f172a',
        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(71, 85, 105, 0.55)',
        controlHeight: 40,
        optionSelectedColor: isDark ? '#ffffff' : '#1d4ed8',
        optionPadding: '10px 12px',
      },
    },
  }), [isDark]);

  const selectStyle = useMemo(() => ({ width: '100%', ...style }), [style]);

  return (
    <ConfigProvider theme={themeConfig}>
      <Select
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        showSearch={showSearch}
        allowClear={allowClear}
        loading={loading}
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        className={className}
        style={selectStyle}
        {...props}
      />
    </ConfigProvider>
  );
};

export default React.memo(AntDropdownComponent);
